import { useEffect, useRef } from 'react';
import { useFormState } from 'react-final-form';
import {
  addDays,
  endOfDay,
  format,
  isBefore,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { useImmerReducer } from 'use-immer';

import useQueryParams from '@common/utils/hooks/useQueryParams';
import {
  Breakpoints,
  useWindowDimensions,
} from '@common/utils/hooks/useWindowDimensions';
import { MeetingTypeLabel, OfficesQuery_offices as Office } from '@typings/api';
import { DateFormats } from '@utils/date';
import { sleep } from '@utils/sleep';

import { FormValues } from '../../BookingFlow';
import { useAddress } from '../../Offices/AddressContext';

import {
  ActionType,
  SearchDirection,
  TimeTableReducer,
  TimetableView,
} from './reducer';
import { useTimeTableQuery } from './useTimeTableQuery';

enum Warning {
  NO_SLOTS_WARNING_CODE = 40420,
  START_OUTSIDE_RANGE_WARNING_CODE = 42201,
  END_OUTSIDE_RANGE_WARNING_CODE = 42202,
}

export function getInitialState(offices: Office[]) {
  const view =
    window.innerWidth <= Breakpoints.sm
      ? TimetableView.DAY
      : TimetableView.WEEK;
  const start = startOfDay(
    view === TimetableView.DAY
      ? new Date()
      : startOfWeek(new Date(), {
          weekStartsOn: 1,
        }),
  );

  return {
    direction: SearchDirection.next,
    end: endOfDay(addDays(start, view - 1)),
    hasReachedEndOfRange: false,
    hasReachedStartOfRange: true,
    isError: false,
    isSearching: true,
    officeGroups: offices.map((office) => ({
      groups: [],
      hasReachedEndOfRange: false,
      hasReachedStartOfRange: false,
      office,
    })),
    start,
    view,
  };
}

export function useTimeTableState() {
  const mounted = useRef<boolean>(false);
  const { id: contextId } = useQueryParams();
  const windowWidth = useWindowDimensions();
  const { address } = useAddress();
  const { country_short, location, postal_code, timezone } = address ?? {};
  const {
    values: { contact, listingId, meetingType, offices, subjectId },
  } = useFormState<FormValues>();
  const [state, dispatch] = useImmerReducer(
    TimeTableReducer,
    getInitialState(offices),
  );

  const [getTimetable] = useTimeTableQuery({
    onError: (error) => {
      console.error(error);
      dispatch({ type: ActionType.SEARCH_ERROR });
    },
  });

  async function getOfficeGroups(offices: Office[]) {
    const officeGroups = await Promise.all(
      offices.map(async (office) => {
        const res = await getTimetable({
          contacts: contact?.id !== undefined ? [contact.id] : undefined,
          contextId,
          endDate: format(state.end, DateFormats.SHORT_DATE),
          listingId,
          meetingType: meetingType!,
          startDate: format(state.start, DateFormats.SHORT_DATE),
          subjectId: subjectId!,
          timezone: timezone!,
          ...(meetingType === MeetingTypeLabel.OFFICE
            ? { coordinates: location }
            : {
                location: {
                  country: country_short,
                  postalCode: postal_code,
                },
              }),
          officeId: office.id,
        });
        const { warnings = [], groups = [] } = res?.data?.timetable ?? {};

        if (groups.length === 0) {
          await sleep(500); // Mimic a next search delay for better UX
        }

        return {
          groups,
          hasReachedEndOfRange: warnings.some(
            (warning) =>
              warning.code === Warning.END_OUTSIDE_RANGE_WARNING_CODE,
          ),
          hasReachedStartOfRange: warnings.some(
            (warning) =>
              warning.code === Warning.START_OUTSIDE_RANGE_WARNING_CODE,
          ),
          office,
        };
      }),
    );

    const shouldContinueSearch =
      state.isSearching &&
      officeGroups.every(
        (group) =>
          group.groups.length === 0 &&
          (state.direction === SearchDirection.next
            ? !group.hasReachedEndOfRange
            : !isBefore(state.start, new Date())),
      );

    if (shouldContinueSearch) {
      return dispatch({
        type: ActionType.SEARCH_CONTINUE,
      });
    }

    return dispatch({
      type: ActionType.SEARCH_FINISH,
      payload: { officeGroups },
    });
  }

  const hasReachedStartOfRange = state.officeGroups.every(
    (group) => group.hasReachedStartOfRange,
  );
  const hasReachedEndOfRange = state.officeGroups.every(
    (group) => group.hasReachedEndOfRange,
  );

  function handleNextClick() {
    return dispatch({
      type: ActionType.SEARCH_START,
      meta: { direction: SearchDirection.next },
    });
  }

  function handlePreviousClick() {
    return dispatch({
      type: ActionType.SEARCH_START,
      meta: { direction: SearchDirection.previous },
    });
  }

  function handleCancelClick() {
    return dispatch({
      type: ActionType.SEARCH_CANCEL,
    });
  }

  useEffect(() => {
    if (mounted.current) {
      dispatch({
        type: ActionType.SEARCH_RESET,
        payload: { initialState: getInitialState(offices) },
      });
    } else {
      mounted.current = true;
    }
  }, [dispatch, offices, contact]);

  useEffect(() => {
    getOfficeGroups(offices);
  }, [state.start]);

  useEffect(() => {
    const newView =
      windowWidth <= Breakpoints.sm ? TimetableView.DAY : TimetableView.WEEK;

    if (newView !== state.view) {
      dispatch({
        type: ActionType.TOGGLE_VIEW,
        payload: {
          view:
            windowWidth <= Breakpoints.sm
              ? TimetableView.DAY
              : TimetableView.WEEK,
        },
      });
    }
  }, [windowWidth]);

  useEffect(() => {
    return () => {
      dispatch({
        type: ActionType.SEARCH_CANCEL,
      });
    };
  }, [dispatch]);

  return {
    handleCancelClick,
    handleNextClick,
    handlePreviousClick,
    timezone,
    ...state,
    hasReachedEndOfRange,
    hasReachedStartOfRange,
  };
}
